import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { context, useSelectors, userSignOut } from '../../../store'
import { MenuGlyph } from '../glyphs'
import { NAV_LINKS } from '../nav'
import { readWashesMenu, WashesList } from '../washes-menu'

// THE PHONE MENU, below 960px (DESIGN.md §4).
//
// It was an MUI `Menu`: a Popover, a Modal, a Backdrop, a Grow transition, a
// portal and a focus trap, to show two links. That is Popover + Modal +
// Transition pulled into the home chunk, which §3 gives 250 KB gzipped for
// everything, and it rendered a floating card anchored to the icon, which is
// an application menu rather than site navigation.
//
// What it is now: a disclosure. A button with aria-expanded and aria-controls,
// and a panel that hangs off the bottom edge of the bar, full width, on
// --paper with a 1px --line under it, the destinations stacked as 44px rows.
// No portal, no backdrop, no transition, no library. The behaviours a Popover
// was being imported for are the four lines of effect below: Escape closes it,
// a click outside closes it, and a route change closes it.
//
// It does NOT trap focus or lock the body. A trap is correct for a modal, and
// this is not one: it is navigation hanging under the bar, tab order runs
// straight through it into the page, and locking the scroll of a page the
// panel does not cover is how a phone ends up stuck.
//
// The panel is rendered only when open; it is also display:none from 960px up,
// so a tab left open while a phone is rotated to landscape cannot leave a
// stray card over the desktop bar.
export const HeaderMenu = React.memo(() => {
  const { dispatch } = useContext(context)
  const { isUserSignedIn } = useSelectors()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const [washesMenu] = useState(readWashesMenu)
  const rootRef = useRef(null)

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen(value => !value), [])

  const handleSignOut = useCallback(() => {
    dispatch(userSignOut())
    setOpen(false)
  }, [dispatch])

  // Navigating is the whole point of the panel, so arriving somewhere closes
  // it. NavLink does not reload, so nothing else would.
  useEffect(() => setOpen(false), [location.pathname])

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = event => {
      if (event.key === 'Escape') setOpen(false)
    }
    // `pointerdown`, not `click`: a tap that starts outside should dismiss
    // before the thing under it fires.
    const onPointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="gwmc-header-menu">
      <button
        type="button"
        className="gwmc-header-menu-button"
        aria-expanded={open}
        aria-controls="gwmc-header-panel"
        aria-label={open ? 'Close menu' : 'Menu'}
        onClick={toggle}>
        <MenuGlyph open={open} />
      </button>

      {open && (
        <nav id="gwmc-header-panel" className="gwmc-header-panel" aria-label="Main">
          <div className="gwmc-container">
            {/* The washes first (COMPETITOR_REVIEW_THEVALETERS_2026-10-06 §5),
                the same list as the desktop menu. */}
            {washesMenu.washes.length > 0 && (
              <>
                <p className="gwmc-eyebrow gwmc-header-panel-heading">Washes</p>
                <WashesList menu={washesMenu} />
              </>
            )}
            {NAV_LINKS.map(link => (
              <NavLink key={link.to} className="gwmc-header-panel-link" to={link.to} onClick={close}>
                {link.label}
              </NavLink>
            ))}
            {isUserSignedIn && (
              <button type="button" className="gwmc-header-panel-link" onClick={handleSignOut}>
                Sign out
              </button>
            )}
          </div>
        </nav>
      )}
    </div>
  )
})
